import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import * as CountryAndTerritoriesActions from './country-territories.actions';
import * as CountryAndTerritoriesSelectors from './country-territories.selectors';
import { TerritoryModel } from '@cpak/adp-cpak-accounts-service-models';
import { StartService } from '@cpak/main-features-lib/services';
import { CountryAndTerritoriesState, TerritoriesState } from '@cpak/interfaces';
import { CountryConfigData } from '@cpak/adp-cpak-bff-service-models';
import { sortByName } from '@cpak/shared/utils';
import { ACCOUNT, ALLCOUNTRY, allowRoles, CATALOG, COUNTRYARRAYLEN, GUIDELINESPATH } from './territories-const';
import { Location } from '@angular/common';

@Injectable()
export class CountriesTerritoriesFacade {
  private initUserRole: string | undefined;
  private countryLen: number | undefined;
  constructor(
    private readonly store: Store,
    private router: Router,
    private startService: StartService,
    private location: Location
  ) {
    this.startService.getStartResponse().subscribe(startResponse => {
      if (startResponse) {
        this.initUserRole = startResponse.data.userInfo.user.roles[0];
        this.countryLen = startResponse.data.userInfo.countryIds.length;
      }
    });
  }

  public getCountryAndTerritoriesState(): Observable<CountryAndTerritoriesState> {
    return this.store.pipe(select(CountryAndTerritoriesSelectors.getCountriesTerritoriesState()));
  }

  public getSelectedCountries(): Observable<string> {
    return this.store.pipe(select(CountryAndTerritoriesSelectors.getSelectedCountries()));
  }

  public getSelectedTerritories(): Observable<TerritoriesState> {
    return this.store.pipe(select(CountryAndTerritoriesSelectors.getSelectedTerritories()));
  }

  public updateTerritoriesState(state: TerritoriesState): void {
    this.store.dispatch(CountryAndTerritoriesActions.updateTerritories(state));
  }

  public updateCountryState(country: string): void {
    this.store.dispatch(CountryAndTerritoriesActions.updateCountry({ country }));
  }

  public updateCountryAndTerritoriesState(country: string, territoriesState: TerritoriesState): void {
    this.store.dispatch(CountryAndTerritoriesActions.updateCountryAndTerritories({ country, ...territoriesState }));
  }

  public initCountriesTerritories(
    availableCountryIds: string[],
    availableTerritoryIds: string[],
    countriesTerritoriesState: CountryAndTerritoriesState | null,
    allCountries: CountryConfigData[]
  ): void {
    const savedState: CountryAndTerritoriesState = {
      country: '',
      territories: null,
      allTerritories: false,
      ...(countriesTerritoriesState || {}),
    };

    const sortedAvailableCountries = sortByName(
      allCountries.filter(country => availableCountryIds.includes(country.id))
    );

    const ALL_COUNTRY =
      this.countryLen &&
      this.countryLen > COUNTRYARRAYLEN &&
      this.initUserRole &&
      allowRoles.indexOf(this.initUserRole) !== -1
        ? ALLCOUNTRY
        : sortedAvailableCountries[0].id;
    const ALL_COUNTRY_SAVED =
      this.countryLen &&
      this.countryLen > COUNTRYARRAYLEN &&
      this.initUserRole &&
      allowRoles.indexOf(this.initUserRole) !== -1
        ? ALLCOUNTRY
        : savedState.country;

    const currentUrl = this.location.path();

    if (!savedState.country || !availableCountryIds.includes(savedState.country)) {
      if (
        this.router.url.indexOf(ACCOUNT) !== -1 ||
        this.router.url.indexOf(CATALOG) !== -1 ||
        currentUrl.indexOf(GUIDELINESPATH) !== -1
      ) {
        this.updateCountryAndTerritoriesState(sortedAvailableCountries[0].id, {
          territories: [],
          allTerritories: true,
        });
        return;
      } else {
        this.updateCountryAndTerritoriesState(ALL_COUNTRY, { territories: [], allTerritories: true });
        return;
      }
    }

    if (savedState.allTerritories || !savedState.territories?.length) {
      if (
        this.router.url.indexOf(ACCOUNT) !== -1 ||
        this.router.url.indexOf(CATALOG) !== -1 ||
        currentUrl.indexOf(GUIDELINESPATH) !== -1
      ) {
        this.updateCountryAndTerritoriesState(savedState.country, { territories: [], allTerritories: true });
        return;
      } else {
        this.updateCountryAndTerritoriesState(ALL_COUNTRY_SAVED, { territories: [], allTerritories: true });
        return;
      }
    }

    const unassignedTerritories = (savedState.territories || []).filter(
      territoryId => !availableTerritoryIds.includes(territoryId)
    );
    if (unassignedTerritories.length) {
      this.updateCountryAndTerritoriesState(ALL_COUNTRY, { territories: [], allTerritories: true });
    } else if (availableTerritoryIds.length === savedState.territories?.length) {
      this.updateCountryAndTerritoriesState(ALL_COUNTRY_SAVED, { territories: [], allTerritories: true });
    } else {
      this.updateCountryAndTerritoriesState(ALL_COUNTRY_SAVED, {
        territories: savedState.territories,
        allTerritories: false,
      });
    }
  }

  public getTerritoriesIdsList(receivedCountriesAndTerritories: TerritoryModel[], countryCode: string): string[] {
    return receivedCountriesAndTerritories
      .filter((territory: TerritoryModel) => territory.countryIds?.includes(countryCode))
      .map((territory: TerritoryModel) => territory.id);
  }
}
